import { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppShell, ImpersonationBanner, useChatDrawerFit } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { AppMobileNav, AppRail } from '@/components/AppNavigation.tsx';
import { BackendUnreachable } from '@/components/BackendUnreachable.tsx';
import { ChatPanel } from '@/components/ChatPanel.tsx';
import { DemoUnavailableDialog } from '@/components/DemoUnavailableDialog.tsx';
import { DemoUnavailablePage } from '@/components/DemoUnavailablePage.tsx';
import { ManagerQuestionDialog } from '@/components/team/ManagerQuestionDialog.tsx';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { useReloadOnIdentityChange } from '@/lib/reloadOnIdentityChange.ts';
import { personName } from '@/lib/personName.ts';
import { redirectIfPilotEnded } from '@/lib/pilotEnded.ts';
import { takeLinkClick } from '@/lib/linkClick.ts';
import { takeAppArrival } from '@/lib/appArrival.ts';
import { requireSession } from '@wundo/client/session.ts';
import { startSessionReplay } from '@/lib/startSessionReplay.ts';
import { applyUserLocale } from '@/lib/locale.ts';
import { useChatContentMin } from '@/lib/chatContentMin.ts';
import { useChatDrawerState } from '@/lib/chatDrawerState.ts';
import { useChatDrawerWidth } from '@/lib/chatDrawerWidth.ts';
import { useChatExpanded } from '@/lib/chatExpanded.ts';
import { availableInDemo } from '@/lib/availableInDemo.ts';

/*
 * The signed-in half of the app. Every page inside it renders in the shell and
 * behind the session guard, so neither has to be repeated per route.
 */
export const Route = createFileRoute('/_app')({
  /*
   * The API refuses a scoped session — the kind minted by the links in Teams
   * and Slack — on any route that does not declare that scope, which is every
   * route this app calls, so a 401 here means the same thing a session probe
   * used to mean.
   */
  beforeLoad: async ({ context, location }) => {
    startSessionReplay(await requireSession(context.queryClient, location.href));
    takeLinkClick(location.href);
    await redirectIfPilotEnded(context.queryClient);

    /* Last, so a guard that sends the visitor on does not use up their arrival. */
    return { arriving: takeAppArrival() };
  },
  component: AppLayout,
  /* Reached when the guard rethrows: the check failed, rather than answering "no". */
  errorComponent: BackendUnreachable,
});

export function AppLayout() {
  /*
   * Only the account disc at the foot of the rail needs this, so the shell
   * renders without waiting — blocking the whole frame on one avatar would be a
   * worse trade.
   */
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * Staff signed in as this person. Both halves of who that makes this tab are
   * watched together: stopping "Login as" changes the user, starting it changes
   * only the impersonator.
   */
  const impersonatedBy = useImpersonatedBy();
  useReloadOnIdentityChange(
    user && impersonatedBy !== undefined
      ? `${user.id}:${impersonatedBy ?? ''}`
      : undefined,
  );

  /*
   * Signed in, so the browser's language preference stops being the best guess
   * we have. Runs on every load rather than only on sign-in: the setting can be
   * changed from another device, or from the settings page itself.
   */
  useEffect(() => {
    applyUserLocale(user?.language);
  }, [user?.language]);

  /*
   * The drawer lives here rather than on a page, so it survives navigation:
   * moving between pages leaves the conversation, and any reply on its way,
   * exactly where they were.
   */
  const [chatState, setChatState] = useChatDrawerState();
  const [wantedWidth, setChatWidth, resetChatWidth] = useChatDrawerWidth();
  const [chatExpanded, toggleChatExpanded, collapseChat] = useChatExpanded();
  /*
   * The width asked for is the user's; the width drawn is what the page can
   * spare. Asked once here and handed to both the shell and the drawer, so the
   * track and the panel in it cannot disagree by a pixel.
   */
  const contentMin = useChatContentMin();
  const { width: chatWidth, pushes: chatPushes } = useChatDrawerFit({
    wanted: wantedWidth,
    contentMin,
  });
  /*
   * Navigating leaves full screen: the point of going somewhere is seeing it,
   * and a chat covering the window would swallow the very navigation just
   * asked for. The drawer itself stays as it was — only the covering ends.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    collapseChat();
  }, [pathname, collapseChat]);

  /*
   * A page that is not there has nothing for the chat to sit beside, so the
   * drawer, its strip and every way to open it are left off.
   */
  const pageNotFound = useRouterState({
    select: (state) => state.matches.some((match) => match.status === 'notFound'),
  });
  /* A demo organisation is there to practise, and the chat leads everywhere else. */
  const demo = user?.demo === true;
  const chatless = pageNotFound || demo;

  const chatStrip = useRef<HTMLButtonElement>(null);
  const mobileChatButton = useRef<HTMLButtonElement>(null);

  const openChat = useCallback(() => setChatState('open'), [setChatState]);

  /*
   * Opening gives the caret to the composer; putting the drawer away has to
   * give it back, or it falls to the document and a keyboard reader starts
   * again from the top of the page. It goes to whatever reopens the chat — the
   * strip on a desktop, the topbar button on a phone — which is where they were
   * standing before they opened it.
   *
   * The change is flushed rather than left to the next render because that
   * control is still hidden while this handler runs, and a hidden button cannot
   * take the caret. Flushing also puts this last: the composer asks for the
   * caret too whenever the drawer's state changes, and that ask happens inside
   * the flush.
   */
  const dismissChat = useCallback(() => {
    flushSync(() => {
      setChatState('collapsed');
      /* Full screen belongs to the conversation being left, not to the app. */
      collapseChat();
    });

    (chatStrip.current ?? mobileChatButton.current)?.focus();
  }, [setChatState, collapseChat]);

  /*
   * One key for the whole drawer, held by the layout rather than by the panel:
   * collapsed, there is nothing of the panel left to press a key on, and the
   * shortcut has to work from exactly there.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (chatless) return;
      if (event.key !== '.' || !(event.metaKey || event.ctrlKey)) return;

      event.preventDefault();
      if (chatState === 'open') dismissChat();
      else openChat();
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [chatState, dismissChat, openChat, chatless]);

  return (
    <AppShell
      /* Full screen covers the page, so there is nothing to make room for. */
      chat={chatExpanded || chatless ? 'none' : chatState}
      chatWidth={chatWidth}
      chatPushes={chatPushes}
      rail={<AppRail user={user} />}
      mobileNav={
        <AppMobileNav
          user={user}
          onOpenChat={chatless ? undefined : openChat}
          chatButtonRef={mobileChatButton}
        />
      }
      banner={
        impersonatedBy && user ? (
          <ImpersonationBanner
            stopLabel={m.impersonation_stop()}
            onStop={() => window.location.assign('/stop-impersonating')}
          >
            {m.impersonation_banner({ name: personName(user) })}
          </ImpersonationBanner>
        ) : undefined
      }
    >
      {demo && !availableInDemo(pathname) ? <DemoUnavailablePage /> : <Outlet />}
      {demo && <DemoUnavailableDialog />}
      <ManagerQuestionDialog user={user} />
      {!chatless && (
        <ChatPanel
          open={chatState === 'open'}
          onDismiss={dismissChat}
          onExpand={openChat}
          width={chatWidth}
          onWidthChange={setChatWidth}
          onWidthReset={resetChatWidth}
          stripRef={chatStrip}
          userId={user?.id}
          expanded={chatExpanded}
          onToggleExpanded={toggleChatExpanded}
        />
      )}
    </AppShell>
  );
}
